import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../common/services/notification.service';
import { parseWorkingTimeToMinutes } from '../../shared/utils/company-working-hours.util';
import {
  buildProposedAssignmentOrder,
  findProviderScheduleConflict,
} from '../../shared/utils/scheduled-order.util';
import { resolveServiceDurationMinutes } from '../../shared/utils/service-duration.util';
import {
  buildOrderHistoryEntry,
  withOrderHistory,
} from '../../shared/utils/order-history.util';
import {
  CompanyNewOrderDialogPayload,
  CompanyNewOrderDialogService,
} from './company-new-order-dialog.service';
import { isProviderBanned } from '../../shared/utils/provider-availability.util';
import {
  buildSchedulePreferenceLabel,
  getCarCareScheduleWindowLabel,
} from '../../shared/utils/company-working-hours.util';

@Component({
  selector: 'app-company-new-order-dialog',
  templateUrl: './company-new-order-dialog.component.html',
  styleUrls: ['./company-new-order-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CompanyNewOrderDialogComponent implements OnInit, OnDestroy {
  visible = false;
  loading = false;
  submitting = false;

  payload: CompanyNewOrderDialogPayload | null = null;
  order: any = null;
  customer: any = null;
  providers: any[] = [];
  selectedProvider: any = null;
  /** Combined date+time the provider should go to the customer. */
  arrivalAt: Date | null = null;
  arrivalDate: Date | null = null;
  arrivalTime = '';
  minArrivalDate = new Date();

  private companyId = '';
  private sub?: Subscription;
  private orderSub?: Subscription;

  constructor(
    private dialogService: CompanyNewOrderDialogService,
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private messageService: MessageService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.sub = this.dialogService.active$.subscribe((payload) => {
      if (!payload) {
        this.visible = false;
        this.resetState();
        return;
      }
      this.payload = payload;
      this.visible = true;
      void this.loadOrder(payload.requestId);
    });

    this.afAuth.authState.subscribe((user) => {
      if (!user) {
        return;
      }
      this.companyId = user.uid;
      this.loadProviders(user.uid);
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.orderSub?.unsubscribe();
  }

  get isProductOrder(): boolean {
    return !!this.order?.isProductOrder;
  }

  get estimatedDeliveryLabel(): string {
    if (!this.order) {
      return '';
    }
    if (this.order.scheduled_arrival_time_label) {
      return String(this.order.scheduled_arrival_time_label);
    }
    const raw = this.order.scheduled_arrival_time;
    if (!raw) {
      return '';
    }
    const d = this.coerceDate(raw);
    if (!d) {
      return '';
    }
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /** Customer-requested schedule window (another date / morning / etc.). */
  get requestedScheduleLabel(): string {
    if (!this.order) {
      return '';
    }
    if (this.order.requested_window_label) {
      return String(this.order.requested_window_label);
    }
    return (
      buildSchedulePreferenceLabel({
        window: this.order.requested_window,
        requestedDate: this.resolveRequestedServiceDay(this.order),
        dayPeriod: this.order.requested_day_period,
      }) ||
      getCarCareScheduleWindowLabel(this.order.requested_window) ||
      ''
    );
  }

  get isScheduledAppointment(): boolean {
    if (!this.order) {
      return false;
    }
    if (this.order.scheduled === true) {
      return true;
    }
    const win = String(this.order.requested_window || '').trim();
    return !!win && win !== 'few_hours_today';
  }

  get requestedDayLabel(): string {
    const d = this.resolveRequestedServiceDay(this.order);
    if (!d) {
      return '';
    }
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get serviceLabel(): string {
    return (
      this.order?.serviceNameEn ||
      this.order?.serviceName ||
      this.order?.serviceNameAr ||
      this.order?.category ||
      'Service'
    );
  }

  get customerPhoneDisplay(): string {
    const fromOrder = (this.order?.customerPhone || '').toString().trim();
    if (fromOrder) {
      return fromOrder;
    }
    const code = (this.customer?.phoneCode || '').toString().trim();
    const tlf = (this.customer?.tlf || this.customer?.phone || '')
      .toString()
      .trim();
    return `${code}${tlf}`.trim();
  }

  get customerPhoneDigits(): string {
    return this.customerPhoneDisplay.replace(/\D+/g, '');
  }

  get hasCustomerWhatsApp(): boolean {
    return this.customerPhoneDigits.length >= 8;
  }

  callCustomer(): void {
    const digits = this.customerPhoneDigits;
    if (!digits) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No phone number',
        detail: 'This customer does not have a valid phone number.',
      });
      return;
    }
    window.open(`tel:${digits}`, '_self');
  }

  openCustomerWhatsApp(): void {
    const digits = this.customerPhoneDigits;
    if (!digits) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No phone number',
        detail: 'This customer does not have a valid phone number.',
      });
      return;
    }
    const name = this.order?.customerName || 'there';
    const service = this.serviceLabel;
    const windowLabel =
      this.order?.requested_window_label ||
      this.order?.requested_window ||
      '';
    const text = encodeURIComponent(
      windowLabel
        ? `Hello ${name}, regarding your ${service} request (${windowLabel}). Let's agree on the exact service time.`
        : `Hello ${name}, regarding your ${service} order. Let's agree on the exact service time.`
    );
    window.open(`https://wa.me/${digits}?text=${text}`, '_blank');
  }

  get needsArrivalTime(): boolean {
    // Car-care style + product/static services
    return (
      this.isProductOrder ||
      !!this.order?.selectedOptions ||
      !!this.order?.form ||
      String(this.order?.serviceNameEn || this.order?.serviceName || '')
        .toLowerCase()
        .includes('wash') ||
      String(this.order?.category || '').length > 0
    );
  }

  get canConfirmAssign(): boolean {
    if (!this.order || this.order.status === 'declined' || this.order.status === 'cancelled') {
      return false;
    }
    if (!this.selectedProvider?.id) {
      return false;
    }
    if (this.needsArrivalTime && !this.arrivalAt) {
      return false;
    }
    return true;
  }

  onHide(): void {
    this.dialogService.close();
  }

  private resetState(): void {
    this.orderSub?.unsubscribe();
    this.orderSub = undefined;
    this.payload = null;
    this.order = null;
    this.customer = null;
    this.selectedProvider = null;
    this.arrivalAt = null;
    this.arrivalDate = null;
    this.arrivalTime = '';
    this.loading = false;
    this.submitting = false;
  }

  private loadProviders(companyId: string): void {
    this.afs
      .collection('users', (ref) => ref.where('companyId', '==', companyId))
      .valueChanges({ idField: 'id' })
      .subscribe((rows) => {
        this.providers = (rows || []).filter(
          (p: any) =>
            (p?.role === 'cprovider' || !p?.role) &&
            !isProviderBanned(p)
        );
      });
  }

  private isOnRoadOrder(order: any): boolean {
    if (!order) {
      return false;
    }
    const orderType = String(order.orderType || order.serviceType || '')
      .toLowerCase()
      .trim();
    if (orderType.includes('on-road') || orderType.includes('onroad')) {
      return true;
    }
    // On-road assigns a provider immediately with showToProvider.
    // Company accept/reject modal is only for unassigned company orders (car-care/shop).
    if (
      order.providerId &&
      order.showToProvider === true &&
      !order.isProductOrder
    ) {
      return true;
    }
    return false;
  }

  private async loadOrder(requestId: string): Promise<void> {
    this.loading = true;
    this.orderSub?.unsubscribe();
    this.orderSub = this.afs
      .collection('ServiceRequests')
      .doc(requestId)
      .valueChanges()
      .subscribe(async (doc: any) => {
        this.order = doc ? { id: requestId, ...doc } : null;
        this.loading = false;

        if (!doc) {
          return;
        }

        // On-road orders are handled by the assigned provider — never company accept/reject.
        if (this.isOnRoadOrder(doc)) {
          this.messageService.add({
            severity: 'info',
            summary: 'On-road order',
            detail:
              (doc.providerName || 'Your provider') +
              ' already received this on-road order. No company accept/reject is needed.',
            life: 7000,
          });
          this.visible = false;
          this.dialogService.close();
          return;
        }

        if (doc.providerId) {
          this.selectedProvider =
            this.providers.find((p) => p.id === doc.providerId) ||
            this.selectedProvider;
        }

        if (doc.companyArrivalAt) {
          const d = this.coerceDate(doc.companyArrivalAt);
          if (d) {
            this.setArrivalParts(d);
          }
        } else if (
          doc.scheduledAt ||
          doc.confirmed_service_time ||
          this.isScheduledOrder(doc)
        ) {
          // Prefill from customer's requested / booked appointment day (not "now + 45m").
          const booked =
            this.coerceDate(doc.confirmed_service_time) ||
            this.coerceDate(doc.scheduledAt) ||
            this.resolveRequestedServiceDay(doc);
          if (booked) {
            // Keep requested calendar day; only invent a midday time if none was booked.
            const hasClock =
              !!doc.confirmed_service_time ||
              !!doc.scheduledAt ||
              (typeof doc.date === 'string' && /AM|PM|\d:\d\d/i.test(doc.date));
            if (!hasClock) {
              booked.setHours(10, 0, 0, 0);
            }
            this.setArrivalParts(booked);
          } else if (!this.arrivalAt) {
            const d = new Date();
            d.setMinutes(d.getMinutes() + 45);
            d.setSeconds(0, 0);
            this.setArrivalParts(d);
          }
        } else if (!this.arrivalAt) {
          // Default: 45 minutes from now
          const d = new Date();
          d.setMinutes(d.getMinutes() + 45);
          d.setSeconds(0, 0);
          this.setArrivalParts(d);
        }

        if (doc.customerId) {
          const snap = await this.afs
            .collection('users')
            .doc(doc.customerId)
            .get()
            .toPromise();
          this.customer = snap?.exists
            ? { id: snap.id, ...(snap.data() as any) }
            : null;
        }
      });
  }

  selectProvider(provider: any): void {
    this.selectedProvider = provider;
  }

  providerLabel(provider: any): string {
    if (!provider) {
      return '';
    }
    const name = `${provider.fname || ''} ${provider.lname || ''}`.trim();
    return name || provider.name || provider.displayName || 'Provider';
  }

  private formatTimeLabel(date: Date): string {
    let h = date.getHours();
    const m = date.getMinutes();
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${m.toString().padStart(2, '0')} ${period}`;
  }

  private setArrivalParts(date: Date): void {
    this.arrivalAt = date;
    this.arrivalDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    this.arrivalTime = this.formatTimeLabel(date);
  }

  onArrivalTimeSet(time: string): void {
    this.arrivalTime = time;
    this.syncArrivalFromParts();
  }

  syncArrivalFromParts(): void {
    if (!this.arrivalDate || !this.arrivalTime) {
      this.arrivalAt = null;
      return;
    }
    const minutes = parseWorkingTimeToMinutes(this.arrivalTime);
    if (minutes == null) {
      this.arrivalAt = null;
      return;
    }
    const d = new Date(this.arrivalDate);
    d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    this.arrivalAt = d;
  }

  formatArrival(value: Date | null): string {
    if (!value) {
      return '';
    }
    return value.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private isScheduledOrder(order: any): boolean {
    if (!order) {
      return false;
    }
    if (order.scheduled === true) {
      return true;
    }
    const win = String(order.requested_window || '').trim();
    return !!win && win !== 'few_hours_today';
  }

  /** Local calendar day for the customer's requested service. */
  resolveRequestedServiceDay(order: any): Date | null {
    if (!order) {
      return null;
    }
    const fromSlot =
      order.dateString ||
      order.requested_date ||
      order.requestedDate;
    const fromInstant =
      order.scheduledAt ??
      order.confirmed_service_time ??
      (order.scheduled ? order.date : null);
    const raw = fromSlot ?? fromInstant ?? order.date;
    return this.coerceDate(raw);
  }

  coerceDate(input: any): Date | null {
    try {
      if (input == null || input === '') {
        return null;
      }
      if (typeof input?.toDate === 'function') {
        const d = input.toDate();
        return isNaN(d.getTime()) ? null : d;
      }
      if (typeof input === 'object' && typeof input.seconds === 'number') {
        const d = new Date(input.seconds * 1000);
        return isNaN(d.getTime()) ? null : d;
      }
      if (input instanceof Date) {
        return isNaN(input.getTime()) ? null : input;
      }
      if (typeof input === 'string') {
        const raw = input.trim();
        // YYYY-MM-DD → local midnight
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          const [y, m, d] = raw.split('-').map(Number);
          const local = new Date(y, m - 1, d);
          return isNaN(local.getTime()) ? null : local;
        }
        // "May 30, 2026 at 7:00:00 AM UTC+3"
        if (/at/i.test(raw) || /UTC[+-]\d/i.test(raw)) {
          const normalized = raw
            .replace(/\s+at\s+/i, ' ')
            .replace(/\s+UTC([+-]\d+)\s*$/i, (_m, offset) => {
              const n = Number(offset);
              if (!Number.isFinite(n)) {
                return ' GMT+0000';
              }
              const sign = n >= 0 ? '+' : '-';
              const abs = Math.abs(n);
              return ` GMT${sign}${String(abs).padStart(2, '0')}00`;
            });
          const t = Date.parse(normalized);
          if (!isNaN(t)) {
            return new Date(t);
          }
        }
        const d = new Date(raw);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(input);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  async rejectOrder(): Promise<void> {
    if (!this.order?.id || this.submitting) {
      return;
    }
    if (!confirm('Reject this order? The customer will be notified.')) {
      return;
    }

    this.submitting = true;
    const serviceName = this.serviceLabel;
    try {
      await this.afs.collection('ServiceRequests').doc(this.order.id).update(
        withOrderHistory(
          {
            status: 'declined',
            declinedAt: new Date(),
            declinedBy: 'company',
          },
          buildOrderHistoryEntry({
            action: 'COMPANY_DECLINED',
            status: 'declined',
            message: 'Company declined the order.',
            actor: {
              id: this.order.companyId || this.companyId,
              role: 'company_admin',
              name: this.companyActorName(),
            },
            changes: { declinedBy: 'company' },
          })
        )
      );

      await this.saveNotification('notifications', {
        type: 'orderDeclinedByCompany',
        message: `Your request for ${serviceName} was declined by the company.`,
        title: 'Order declined',
        reqId: this.order.id,
        senderId: this.order.companyId || this.companyId,
        receiverId: this.order.customerId,
      });

      this.sendPush(
        this.order.customerId,
        this.order.id,
        'Order declined',
        `Your request for ${serviceName} was declined by the company.`
      );

      await this.markNotificationHandled();
      this.messageService.add({
        severity: 'success',
        summary: 'Order rejected',
        detail: 'Customer has been notified.',
      });
      this.dialogService.close();
    } catch (error: any) {
      console.error(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'Could not reject the order.',
      });
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Accept order, assign provider, set go-to-customer time, notify both sides.
   */
  async acceptAndAssign(): Promise<void> {
    if (!this.canConfirmAssign || !this.order?.id || this.submitting) {
      return;
    }

    this.submitting = true;
    const serviceName = this.serviceLabel;
    const providerName = this.providerLabel(this.selectedProvider);
    const arrival = this.arrivalAt ? new Date(this.arrivalAt) : null;
    const arrivalLabel = this.formatArrival(arrival);

    try {
      if (arrival) {
        const companyOrders = await this.loadCompanyActiveOrders();
        const proposed = buildProposedAssignmentOrder(this.order, arrival);
        const conflict = findProviderScheduleConflict(
          this.selectedProvider.id,
          proposed,
          companyOrders
        );
        if (conflict) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Provider unavailable',
            detail: conflict.message,
          });
          return;
        }
      }

      const durationMinutes = resolveServiceDurationMinutes(this.order);
      const patch: Record<string, unknown> = {
        status: 'accepted',
        acceptedAt: new Date(),
        providerId: this.selectedProvider.id,
        providerName,
        // Company assign uses normal notification ping — not on-road accept/reject alert
        showToProvider: false,
        needAssign: false,
        serviceDurationMinutes: durationMinutes,
      };

      if (arrival) {
        patch['companyArrivalAt'] = arrival;
        patch['companyArrivalAtSetAt'] = new Date();
        patch['companyArrivalAtLabel'] = arrivalLabel;
        patch['scheduled_arrival_time'] = arrival;
        patch['scheduled_arrival_time_label'] = arrivalLabel;
        patch['scheduledAt'] = arrival;
        patch['date'] = arrival;
        // Scheduled "another date" bookings are appointments for company/provider calendars.
        if (this.isScheduledAppointment) {
          patch['scheduled'] = true;
          patch['isAppointment'] = true;
        }
      }

      await this.afs
        .collection('ServiceRequests')
        .doc(this.order.id)
        .update(
          withOrderHistory(
            patch,
            buildOrderHistoryEntry({
              action: 'COMPANY_ACCEPTED',
              status: 'accepted',
              message: 'Company accepted the order.',
              actor: {
                id: this.order.companyId || this.companyId,
                role: 'company_admin',
                name: this.companyActorName(),
              },
            }),
            buildOrderHistoryEntry({
              action: 'PROVIDER_ASSIGNED',
              status: 'accepted',
              message: `Provider ${providerName} assigned to the order.`,
              actor: {
                id: this.order.companyId || this.companyId,
                role: 'company_admin',
                name: this.companyActorName(),
              },
              changes: {
                providerId: this.selectedProvider.id,
                providerName,
                companyArrivalAt: arrival,
              },
            })
          )
        );

      // Customer notifications
      const customerMsg = arrival
        ? `Your request for ${serviceName} was accepted. Provider ${providerName} will go to you around ${arrivalLabel}.`
        : `Your request for ${serviceName} was accepted. Provider: ${providerName}.`;

      await this.saveNotification('notifications', {
        type: 'orderAcceptedByCompany',
        message: customerMsg,
        title: 'Order accepted',
        reqId: this.order.id,
        senderId: this.order.companyId || this.companyId,
        receiverId: this.order.customerId,
      });

      this.sendPush(
        this.order.customerId,
        this.order.id,
        'Order accepted',
        customerMsg
      );

      // Provider notifications
      const providerMsg = arrival
        ? `New order for ${serviceName}. Go to the customer at ${arrivalLabel}.`
        : `You have a new order for ${serviceName}.`;

      await this.saveNotification('notifications', {
        type: 'companyOrderAssign',
        message: providerMsg,
        title: 'New Order',
        reqId: this.order.id,
        senderId: this.order.companyId || this.companyId,
        receiverId: this.selectedProvider.id,
      });

      this.sendPush(
        this.selectedProvider.id,
        this.order.id,
        'New Order Assigned',
        providerMsg
      );

      await this.markNotificationHandled();
      this.messageService.add({
        severity: 'success',
        summary: 'Order accepted',
        detail: 'Provider and customer have been notified.',
      });
      this.dialogService.close();
    } catch (error: any) {
      console.error(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'Could not accept and assign the order.',
      });
    } finally {
      this.submitting = false;
    }
  }

  private async loadCompanyActiveOrders(): Promise<any[]> {
    if (!this.companyId) {
      return [];
    }
    try {
      const snap = await this.afs
        .collection('ServiceRequests', (ref) =>
          ref.where('companyId', '==', this.companyId)
        )
        .get()
        .toPromise();
      return (snap?.docs || [])
        .map((d) => ({ id: d.id, ...(d.data() as object) }))
        .filter((o: any) => {
          const s = String(o?.status || '').toLowerCase();
          return s !== 'cancelled' && s !== 'done' && s !== 'declined' && s !== 'draft';
        });
    } catch (e) {
      console.warn('Failed to load company orders for conflict check', e);
      return [];
    }
  }

  private async markNotificationHandled(): Promise<void> {
    const notifId = this.payload?.notificationId;
    if (!notifId) {
      return;
    }
    try {
      await this.afs.collection('company-notifications').doc(notifId).update({
        seen: true,
        shown: true,
      });
    } catch (e) {
      console.warn('Failed to mark company notification handled', e);
    }
  }

  private companyActorName(): string {
    return 'Company admin';
  }

  copyOrderId(): void {
    const id = String(this.order?.id || '').trim();
    if (!id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No order ID',
        detail: 'Order ID is not available.',
      });
      return;
    }
    navigator.clipboard.writeText(id).then(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied',
          detail: 'Order ID copied to clipboard.',
          life: 2500,
        });
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Copy failed',
          detail: 'Could not copy Order ID.',
        });
      }
    );
  }

  private saveNotification(
    collection: string,
    data: {
      type: string;
      message: string;
      title: string;
      reqId: string;
      senderId: string;
      receiverId: string;
    }
  ) {
    const notifId = this.afs.createId();
    return this.afs.collection(collection).doc(notifId).set({
      ...data,
      id: notifId,
      seen: false,
      shown: true,
      created_at: new Date(),
    });
  }

  private sendPush(
    userId: string,
    requestId: string,
    title: string,
    body: string
  ): void {
    if (!userId || !requestId) {
      return;
    }
    this.notificationService
      .sendToUser(userId, {
        title,
        body,
        link: 'requests/my-active-requests/' + requestId,
      })
      .subscribe({
        next: () => undefined,
        error: (err) => console.warn('Push failed', err),
      });
  }
}
